import React, { useState, useEffect, useCallback } from "react";
import { FaShoppingCart, FaBars } from "react-icons/fa";
import { FiX } from "react-icons/fi";
import {
  HeaderWrapper,
  NavContainer,
  Logo,
  NavMenu,
  NavActions,
  CartButton,
  CartBadge,
  MobileMenuButton,
  Relative,
  StyledLi,
  StyledSpan,
  MobileMenuOverlay,
  MobileMenuContent,
  MobileMenuHeader,
  MobileMenuLogo,
  MobileMenuClose,
  MobileMenuNav,
  MobileNavLink,
} from "./Header.styles";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { formatPrice } from "../../utils/utils";
import { selectCartTotals } from "../../store/slices/cart/slice";
import { useScrollLock } from "../../hooks/useScrollLock";

const Header = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { totalItems, totalPrice } = useSelector(selectCartTotals);

  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Poboljšano scroll state management
  const [scrollState, setScrollState] = useState({
    isVisible: true,
    lastScrollY: 0,
    scrollDirection: "up",
    isAnimating: false,
    isAtTop: true,
    isNavigating: false,
  });

  // Poboljšana scroll logika
  const handleScroll = useCallback(() => {
    // Sprečava animacije tokom navigacije
    if (scrollState.isNavigating) {
      return;
    }

    const currentScrollY = window.scrollY;
    const isAtTop = currentScrollY <= 20; // Smatraj da je na vrhu ako je scroll <= 20px
    const scrollDifference = Math.abs(currentScrollY - scrollState.lastScrollY);

    // Ignore small scroll movements
    if (scrollDifference < 8) {
      return;
    }

    const scrollDirection =
      currentScrollY > scrollState.lastScrollY ? "down" : "up";

    // Ako je na vrhu, header je uvek vidljiv BEZ animacije
    if (isAtTop) {
      setScrollState((prevState) => ({
        ...prevState,
        isVisible: true,
        isAtTop: true,
        lastScrollY: currentScrollY,
        scrollDirection,
        isAnimating: false, // Nema animacije na vrhu
      }));
      return;
    }

    // Optimizovana show/hide logika (samo kada NIJE na vrhu)
    let isVisible = scrollState.isVisible;
    let shouldUpdate = false;

    // Hide header when scrolling down and past threshold
    if (
      scrollDirection === "down" &&
      currentScrollY > 100 &&
      scrollState.isVisible
    ) {
      isVisible = false;
      shouldUpdate = true;
    }
    // Show header when scrolling up (ali ne na vrhu)
    else if (scrollDirection === "up" && !scrollState.isVisible && !isAtTop) {
      isVisible = true;
      shouldUpdate = true;
    }

    // Update state only if something significant changed
    if (
      shouldUpdate ||
      scrollDirection !== scrollState.scrollDirection ||
      Math.abs(currentScrollY - scrollState.lastScrollY) > 20 ||
      isAtTop !== scrollState.isAtTop
    ) {
      setScrollState((prevState) => ({
        ...prevState,
        isVisible,
        lastScrollY: currentScrollY,
        scrollDirection,
        isAnimating: shouldUpdate && !isAtTop, // Animacija samo ako nije na vrhu
        isAtTop,
      }));

      // Reset animation flag after animation completes
      if (shouldUpdate && !isAtTop) {
        setTimeout(() => {
          setScrollState((prevState) => ({
            ...prevState,
            isAnimating: false,
          }));
        }, 500);
      }
    }
  }, [
    scrollState.lastScrollY,
    scrollState.isVisible,
    scrollState.scrollDirection,
    scrollState.isNavigating,
    scrollState.isAtTop,
  ]);

  // Optimizovani scroll effect sa throttling
  useEffect(() => {
    let ticking = false;

    const throttledHandleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    // Dodaj event listener
    window.addEventListener("scroll", throttledHandleScroll, {
      passive: true,
    });

    return () => {
      window.removeEventListener("scroll", throttledHandleScroll);
    };
  }, [handleScroll]);

  // Initialize proper state on component mount
  useEffect(() => {
    const currentScrollY = window.scrollY;
    const isAtTop = currentScrollY <= 20;

    setScrollState((prevState) => ({
      ...prevState,
      isVisible: true,
      isAtTop,
      lastScrollY: currentScrollY,
      isAnimating: false,
      isNavigating: false,
    }));
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Block scrolling when mobile menu is open
  useScrollLock(isMobileMenuOpen);

  const onNavigate = (to) => {
    // Close mobile menu when navigating
    closeMobileMenu();

    // Postavi flag za navigaciju da spreciš animacije
    setScrollState((prevState) => ({
      ...prevState,
      isNavigating: true,
      isVisible: true, // Uvek prikaži header tokom navigacije
    }));

    navigate(to);

    // Scroll to top immediately without smooth behavior
    setTimeout(() => {
      window.scrollTo(0, 0);

      // Reset navigation flag nakon što se stranica učita
      setTimeout(() => {
        setScrollState((prevState) => ({
          ...prevState,
          isNavigating: false,
          isAtTop: true,
          lastScrollY: 0,
          isAnimating: false,
        }));
      }, 100);
    }, 50);
  };

  return (
    <>
      <HeaderWrapper
        $isVisible={scrollState.isVisible}
        $scrollDirection={scrollState.scrollDirection}
        $isAnimating={scrollState.isAnimating}
        $isAtTop={scrollState.isAtTop}
        $isNavigating={scrollState.isNavigating}
      >
        <NavContainer $isVisible={scrollState.isVisible}>
          <Logo onClick={() => onNavigate("/")}>МРЧ</Logo>

          <NavMenu>
            <StyledLi onClick={() => onNavigate("/")}>
              <StyledSpan $active={pathname === "/"}>Početna</StyledSpan>
            </StyledLi>
            <StyledLi onClick={() => onNavigate("/prodavnica")}>
              <StyledSpan $active={pathname === "/prodavnica"}>
                Prodavnica
              </StyledSpan>
            </StyledLi>
            <StyledLi onClick={() => onNavigate("/bendovi")}>
              <StyledSpan $active={pathname === "/bendovi"}>Bendovi</StyledSpan>
            </StyledLi>
            <StyledLi onClick={() => onNavigate("/novosti")}>
              <StyledSpan $active={pathname === "/novosti"}>Novosti</StyledSpan>
            </StyledLi>
            <StyledLi onClick={() => onNavigate("/o-nama")}>
              <StyledSpan $active={pathname === "/o-nama"}>O nama</StyledSpan>
            </StyledLi>
          </NavMenu>

          <NavActions>
            <Relative>
              <CartButton onClick={() => onNavigate("/korpa")}>
                <FaShoppingCart />
                <span className="cart-price">{formatPrice(totalPrice)}</span>
              </CartButton>
              {totalItems > 0 && <CartBadge>{totalItems}</CartBadge>}
            </Relative>
          </NavActions>
          <MobileMenuButton onClick={toggleMobileMenu}>
            <FaBars size={16} />
          </MobileMenuButton>
        </NavContainer>
      </HeaderWrapper>
      {/* Mobile Menu Overlay */}
      <MobileMenuOverlay $isOpen={isMobileMenuOpen}>
        <MobileMenuContent $isOpen={isMobileMenuOpen}>
          <MobileMenuHeader>
            <MobileMenuLogo onClick={() => onNavigate("/")}>МРЧ</MobileMenuLogo>
            <MobileMenuClose onClick={closeMobileMenu}>
              <FiX />
            </MobileMenuClose>
          </MobileMenuHeader>

          <MobileMenuNav>
            <MobileNavLink
              $active={pathname === "/"}
              onClick={() => onNavigate("/")}
            >
              Početna
            </MobileNavLink>
            <MobileNavLink
              $active={pathname === "/prodavnica"}
              onClick={() => onNavigate("/prodavnica")}
            >
              Prodavnica
            </MobileNavLink>
            <MobileNavLink
              $active={pathname === "/bendovi"}
              onClick={() => onNavigate("/bendovi")}
            >
              Bendovi
            </MobileNavLink>
            <MobileNavLink
              $active={pathname === "/novosti"}
              onClick={() => onNavigate("/novosti")}
            >
              Novosti
            </MobileNavLink>
            <MobileNavLink
              $active={pathname === "/o-nama"}
              onClick={() => onNavigate("/o-nama")}
            >
              O nama
            </MobileNavLink>
          </MobileMenuNav>
        </MobileMenuContent>
      </MobileMenuOverlay>
    </>
  );
};

export default Header;
